import { useState, useCallback } from 'react';
import { trpc } from '../../lib/trpc';
import { MediaGrid } from './MediaGrid';
import { MediaDetail } from './MediaDetail';
import { MediaFilters } from './MediaFilters';
import type { Media } from '@wineryxplorer/shared';

export interface SharedMediaLibraryProps {
  venueId?: string; // For using shared media in venue context
  onUseSharedMedia?: (sharedMediaId: string, action: 'setAsHero' | 'addToGallery') => void;
  className?: string;
}

export function SharedMediaLibrary({
  venueId,
  onUseSharedMedia,
  className = '',
}: SharedMediaLibraryProps) {
  // State
  const [detailMedia, setDetailMedia] = useState<Media | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // tRPC queries
  const { data: sharedMediaData, isPending: isLoadingMedia, refetch: _refetchMedia } = trpc.media.getSharedMedia.useQuery({
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    limit: 100,
  });

  const { data: availableTags } = trpc.media.getTagsForEntity.useQuery({
    entityType: 'shared',
    entityId: 'shared', // Use a placeholder ID for shared media
  });

  // Filter media based on search query (client-side for now)
  // Cast media to proper type since tRPC returns unknown[]
  const mediaList = (sharedMediaData?.items ?? []) as Media[];
  
  const filteredMedia = mediaList.filter((media) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      media.filename.toLowerCase().includes(query) ||
      media.caption?.toLowerCase().includes(query) ||
      media.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  // Handlers
  const handleSelectMedia = useCallback((media: Media) => {
    setDetailMedia(media);
    setIsDetailOpen(true);
  }, []);

  const handleUseSharedMedia = useCallback((sharedMediaId: string, action: 'setAsHero' | 'addToGallery') => {
    if (onUseSharedMedia) {
      onUseSharedMedia(sharedMediaId, action);
    }
  }, [onUseSharedMedia]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shared Media Library</h2>
          <p className="text-sm text-gray-600 mt-1">
            Professional stock photos available to all venues
            {sharedMediaData && ` • ${sharedMediaData.total} items`}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-50 text-primary-800 border border-primary-200">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
            </svg>
            Shared Collection
          </div>
        </div>
      </div>

      {/* Filters */}
      <MediaFilters
        availableTags={availableTags?.tags}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        // Hide publication filter and shared media toggle for shared library
        publicationFilter="published"
        showSharedMedia={false}
      />

      {/* Info Banner */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-primary-800">
              About Shared Media
            </h3>
            <div className="mt-2 text-sm text-primary-700">
              <p>
                These professional stock photos are available to all venue managers. 
                When you use shared media, it creates a reference without copying the file, 
                saving storage space and ensuring consistency across the platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      <MediaGrid
        media={filteredMedia}
        onSelectMedia={handleSelectMedia}
        showCheckboxes={false}
        showPublicationStatus={false} // Shared media is always published
        showTags={true}
        showUploader={false}
        isLoading={isLoadingMedia}
        emptyMessage={
          searchQuery || selectedTags.length > 0
            ? 'No shared media matches your filters'
            : 'No shared media available'
        }
      />

      {/* Media Detail Modal */}
      <MediaDetail
        media={detailMedia}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setDetailMedia(null);
        }}
        // Shared media cannot be published/unpublished by venue managers
        onPublish={undefined}
        onUnpublish={undefined}
        // Shared media cannot be deleted by venue managers
        onDelete={undefined}
        // Shared media tags cannot be edited by venue managers
        onUpdateTags={undefined}
        onUpdateCaption={undefined}
        // Use shared media actions
        onUseSharedMedia={handleUseSharedMedia}
        isLoading={false}
        canSetHero={!!venueId}
        canManageGallery={!!venueId}
        venueId={venueId}
      />
    </div>
  );
}